import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { cartAPI } from '@/utils/api';
import { toast } from 'sonner';

const CartPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = async () => {
    try {
      const response = await cartAPI.get();
      setCartItems(response.data);
    } catch (error) {
      toast.error('Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveItem = async (cartItemId) => {
    try {
      await cartAPI.remove(cartItemId);
      setCartItems(cartItems.filter(item => item.cart_item_id !== cartItemId));
      toast.success('Removed from cart');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const calculateTotal = () => {
    return cartItems.reduce((sum, item) => sum + (item.account.price * item.quantity), 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="cart-page">
      <Navbar />
      
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-4xl sm:text-5xl font-heading font-black tracking-tight mb-8" data-testid="cart-title">
            Shopping Cart
          </h1>
          
          {cartItems.length === 0 ? (
            <div className="text-center py-16" data-testid="empty-cart-message">
              <ShoppingBag className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-2xl font-heading font-bold mb-2">Your cart is empty</h2>
              <p className="text-muted-foreground mb-6">Start shopping to add items to your cart</p>
              <Link to="/category/streaming">
                <Button className="btn-primary-glow rounded-full" data-testid="browse-accounts-button">
                  Browse Accounts
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4" data-testid="cart-items-list">
                {cartItems.map((item) => (
                  <div
                    key={item.cart_item_id}
                    className="flex gap-4 p-6 rounded-2xl border border-white/10 bg-white/5"
                    data-testid={`cart-item-${item.cart_item_id}`}
                  >
                    <div className="w-24 h-24 rounded-xl overflow-hidden bg-secondary flex-shrink-0">
                      <img
                        src={item.account.image_url}
                        alt={item.account.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="font-heading font-bold text-lg mb-1" data-testid={`cart-item-title-${item.cart_item_id}`}>
                        {item.account.title}
                      </h3>
                      <p className="text-muted-foreground text-sm mb-2">
                        Quantity: {item.quantity}
                      </p>
                      <p className="text-xl font-heading font-black" data-testid={`cart-item-price-${item.cart_item_id}`}>
                        ₹{item.account.price * item.quantity}
                      </p>
                    </div>
                    
                    <button
                      onClick={() => handleRemoveItem(item.cart_item_id)}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                      data-testid={`remove-item-${item.cart_item_id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="sticky top-24 p-6 rounded-2xl border border-white/10 bg-white/5" data-testid="order-summary">
                  <h2 className="text-xl font-heading font-bold mb-6">Order Summary</h2>
                  
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between text-muted-foreground">
                      <span>Subtotal</span>
                      <span data-testid="subtotal-amount">₹{calculateTotal()}</span>
                    </div>
                    <div className="border-t border-white/10 pt-4">
                      <div className="flex justify-between font-heading font-bold text-xl">
                        <span>Total</span>
                        <span data-testid="total-amount">₹{calculateTotal()}</span>
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    className="w-full btn-primary-glow rounded-full text-base font-bold"
                    onClick={() => navigate('/checkout')}
                    data-testid="proceed-to-checkout-button"
                  >
                    Proceed to Checkout
                  </Button>
                  
                  <Link to="/category/streaming" className="block mt-4">
                    <Button variant="ghost" className="w-full" data-testid="continue-shopping-button">
                      Continue Shopping
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default CartPage;
